/*
 * general input for react-hook-form
 */

import { FormHelperText, OutlinedInput } from '@mui/material'
import DatePicker from 'components/ui/DatePicker'
import ExpenseTypeInput from 'components/ui/ExpenseTypeInput'
import FormInputCheckbox from 'components/ui/FormInputCheckbox'
import Select from 'components/ui/Select'
import { useFormContext, Controller, RegisterOptions } from 'react-hook-form'
import { NumericFormat } from 'react-number-format'
import { extractError } from 'utils/extractError'

export type FormInputType =
  | 'text'
  | 'select'
  | 'checkbox'
  | 'number'
  | 'date'
  | 'hidden'
  | 'select-expense'
type RestProps = Record<string, unknown>
interface FormInputProps {
  name: string
  type: FormInputType
  label?: string
  rules?: RegisterOptions
  disabled?: boolean
  onBlur?: () => void
  defaultValue?: unknown
  onChange?: (...events: any[]) => void
  testid?: string
}

interface InputProps extends Omit<FormInputProps, 'defaultValue'> {
  value: any
  error?: boolean
  placeholder?: string
  onChange: (...events: any[]) => void
}

function Input({ type, ...props }: InputProps & RestProps) {
  if (type === 'select-expense') {
    // error is boolean but it's not compartible
    const { error: _, ...remainProps } = props
    return <ExpenseTypeInput {...remainProps} />
  }
  if (type === 'select') {
    return <Select {...props} />
  }
  if (type === 'date') {
    const dateValue =
      // null is special ( which means no value)
      typeof props.value === 'string' ? new Date(props.value) : props.value // null or Date type data is here since new Date(null) = 1970/1/1...
    const [value, placeholder] =
      isNaN(dateValue) && typeof props.value === 'string'
        ? [null, props.value /* can be invalid date*/]
        : [dateValue, undefined]
    return <DatePicker {...props} value={value} placeholder={placeholder} />
  }

  if (type === 'checkbox') {
    return <FormInputCheckbox {...props} />
  }

  if (type === 'number') {
    const { onChange, ...other } = props
    return (
      <NumericFormat
        valueIsNumericString
        fullWidth
        thousandSeparator
        margin="dense"
        customInput={OutlinedInput}
        onValueChange={({ floatValue }) => onChange(floatValue)}
        {...other}
      />
    )
  }

  if (type === 'hidden') {
    const { error: _, ...remainProps } = props
    return <input type="hidden" {...remainProps} />
  }

  return <OutlinedInput fullWidth margin="dense" {...props} />
}

const defaultDefaultValue = (type: string) => {
  switch (type) {
    case 'checkbox':
      return false
    case 'number':
      return 0
    default:
      return ''
  }
}

export default function FormInput({
  name,
  type,
  rules,
  defaultValue,
  onChange: onChangeProp,
  testid,
  ...props
}: FormInputProps & RestProps) {
  const {
    formState: { errors },
    control,
  } = useFormContext()
  const error = extractError(errors, name)

  return (
    <>
      <Controller
        name={name}
        control={control}
        rules={rules}
        defaultValue={defaultValue === undefined ? defaultDefaultValue(type) : defaultValue}
        render={({ field: { onChange, onBlur, value, name, ref } }) => (
          <Input
            inputRef={ref}
            size="small"
            {...props}
            type={type}
            error={!props?.disabled && Boolean(error)}
            value={value}
            name={name}
            onChange={(e) => {
              /* onChangePropはexpense type, invoice agent両方専用 */
              onChange(e)
              onChangeProp && onChangeProp(name, e)
            }}
            onBlur={() => {
              onBlur()
              props.onBlur && props.onBlur()
            }}
            data-testid={testid}
          />
        )}
      />
      {!props?.disabled && error?.message && (
        // { ml: 0 } : group editでmlが追加される(?)のでここで調整
        <FormHelperText sx={{ ml: 0, pl: 1.6 }} error>
          {error?.message?.toString()}
        </FormHelperText>
      )}
    </>
  )
}
